/**
 * TrackMetadataService.js
 *
 * Service for extracting and calculating track metadata from GeoJSON coordinates.
 * Handles timestamp extraction from XYZM coordinates (OpenLayers format),
 * distance calculation, and duration formatting.
 *
 * Distance calculation now uses OpenLayers geodesic functions via Utils.js
 * for consistency across the application.
 *
 * @module TrackMetadataService
 */

import { appDebugLog } from '@/utils/appDebugLog';
import {
  calculateDistanceFromCoordinates,
  flattenCoordinates as flattenCoordsFromUtils
} from '@/utils/Utils';

// ============================================================================
// BACKUP: Haversine Distance Calculation (commented out)
// ============================================================================
// Decision: Jan 4th, 2026 - User decided to keep these methods as backup.
// The app now uses OpenLayers' getLength() for distance calculation (via Utils.js)
// which provides more accurate WGS84-based geodesic calculation.
//
// These functions can be restored if needed for:
// - Offline/standalone calculation without OpenLayers dependency
// - Performance comparison testing
// - Edge cases where OL behaves unexpectedly
// ============================================================================

// /**
//  * Earth's radius in kilometers for Haversine calculation
//  * Note: OpenLayers uses 6371.0088 km (WGS84 mean radius) which is slightly more precise
//  */
// const EARTH_RADIUS_KM = 6371;
//
// /**
//  * Convert degrees to radians
//  * @param {number} degrees - Angle in degrees
//  * @returns {number} Angle in radians
//  */
// function toRadians (degrees) {
//   return degrees * (Math.PI / 180);
// }
//
// /**
//  * Calculate distance between two points using Haversine formula.
//  *
//  * The Haversine formula calculates the great-circle distance between two points
//  * on a sphere given their longitudes and latitudes. It assumes a spherical Earth.
//  *
//  * @param {number} lat1 - Latitude of first point
//  * @param {number} lon1 - Longitude of first point
//  * @param {number} lat2 - Latitude of second point
//  * @param {number} lon2 - Longitude of second point
//  * @returns {number} Distance in kilometers
//  */
// function haversineDistance (lat1, lon1, lat2, lon2) {
//   const dLat = toRadians(lat2 - lat1);
//   const dLon = toRadians(lon2 - lon1);
//
//   const a =
//     Math.sin(dLat / 2) * Math.sin(dLat / 2) +
//     Math.cos(toRadians(lat1)) *
//       Math.cos(toRadians(lat2)) *
//       Math.sin(dLon / 2) *
//       Math.sin(dLon / 2);
//
//   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
//
//   return EARTH_RADIUS_KM * c;
// }
//
// /**
//  * BACKUP: Calculate total distance using local Haversine implementation.
//  * Use calculateTotalDistance() instead which delegates to Utils.js
//  */
// function calculateTotalDistanceHaversine (coordinates) {
//   const coords = flattenCoordinates(coordinates);
//
//   if (!coords || coords.length < 2) {
//     return { km: 0, m: 0, feet: 0, inches: 0, miles: 0, seamiles: 0, yards: 0 };
//   }
//
//   let totalKm = 0;
//
//   for (let i = 1; i < coords.length; i++) {
//     const prev = coords[i - 1];
//     const curr = coords[i];
//
//     const lon1 = prev[0];
//     const lat1 = prev[1];
//     const lon2 = curr[0];
//     const lat2 = curr[1];
//
//     totalKm += haversineDistance(lat1, lon1, lat2, lon2);
//   }
//
//   const meters = totalKm * 1000;
//   const feet = meters * 3.28084;
//   const inches = feet * 12;
//   const miles = totalKm * 0.621371;
//   const seamiles = totalKm * 0.539957;
//   const yards = meters * 1.09361;
//
//   return {
//     km: (Math.round(totalKm * 1000) / 1000).toString(),
//     m: (Math.round(meters * 1000) / 1000).toString(),
//     feet: (Math.round(feet * 1000) / 1000).toString(),
//     inches: (Math.round(inches * 1000) / 1000).toString(),
//     miles: (Math.round(miles * 1000) / 1000).toString(),
//     seamiles: (Math.round(seamiles * 1000) / 1000).toString(),
//     yards: (Math.round(yards * 1000) / 1000).toString()
//   };
// }

// ============================================================================
// Coordinate Utilities
// ============================================================================

/**
 * Flatten coordinates if they are nested (MultiLineString format).
 * Re-exports from Utils.js for backwards compatibility with existing imports.
 *
 * @param {Array} coordinates - Coordinate array (may be nested)
 * @returns {Array} Flat array of coordinate points
 */
export function flattenCoordinates (coordinates) {
  return flattenCoordsFromUtils(coordinates);
}

/**
 * Count the number of coordinate points in a geometry coordinate array.
 * Handles LineString and MultiLineString (nested) coordinate structures.
 *
 * @param {Array|null|undefined} coordinates - GeoJSON geometry coordinates
 * @returns {number} Number of coordinate points
 */
export function countTrackPoints (coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return 0;

  // Point geometry: [lon, lat, ...]
  if (typeof coordinates[0] === 'number') return 1;

  // LineString: [[lon, lat, ...], ...]
  if (Array.isArray(coordinates[0]) && typeof coordinates[0][0] === 'number') {
    return coordinates.length;
  }

  // MultiLineString: [[[lon, lat, ...], ...], ...]
  if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0]) && typeof coordinates[0][0][0] === 'number') {
    return coordinates.reduce((sum, segment) => sum + (Array.isArray(segment) ? segment.length : 0), 0);
  }

  // Deeply nested: sum recursively (defensive; not standard for track data but seen in some conversions)
  return coordinates.reduce((sum, child) => sum + countTrackPoints(child), 0);
}

/**
 * Get total point count from GeoJSON.
 *
 * Handles FeatureCollection, Feature, and (Multi)LineString geometries.
 * For FeatureCollections, sums all (Multi)LineString features and ignores
 * connector features (`properties.featureType === 'connector'`).
 *
 * @param {Object|null|undefined} geojson - GeoJSON object
 * @returns {number} Total number of coordinate points
 */
export function getPointCount (geojson) {
  if (!geojson) return 0;

  // Convenience: allow passing coordinates directly.
  if (Array.isArray(geojson)) {
    return countTrackPoints(geojson);
  }

  // Convenience: allow passing a track record `{ geojson: ... }`.
  if (geojson?.geojson && !geojson?.type) {
    return getPointCount(geojson.geojson);
  }

  if (geojson.type === 'FeatureCollection') {
    const features = Array.isArray(geojson.features) ? geojson.features : [];
    let total = 0;

    for (const feature of features) {
      if (!feature?.geometry) continue;
      if (feature?.properties?.featureType === 'connector') continue;
      total += getPointCount(feature);
    }

    return total;
  }

  if (geojson.type === 'Feature') {
    if (geojson?.properties?.featureType === 'connector') return 0;
    return getPointCount(geojson.geometry);
  }

  if (geojson.type === 'GeometryCollection') {
    const geometries = Array.isArray(geojson.geometries) ? geojson.geometries : [];
    let total = 0;

    for (const geometry of geometries) {
      total += getPointCount(geometry);
    }

    return total;
  }

  if (geojson.type === 'LineString' || geojson.type === 'MultiLineString') {
    return countTrackPoints(geojson.coordinates);
  }

  return 0;
}

// ============================================================================
// Timestamp Extraction
// ============================================================================

/**
 * Extract timestamps from coordinates (XYZM format)
 * OpenLayers stores time as the M coordinate (4th dimension) in seconds since Unix epoch
 *
 * @param {Array} coordinates - Array of coordinate points [lon, lat, alt?, time?]
 * @returns {{ startTime: number|null, endTime: number|null }}
 */
export function extractTimestampsFromCoordinates (coordinates) {
  const coords = flattenCoordinates(coordinates);

  if (!coords || coords.length === 0) {
    appDebugLog.info('TrackMetadataService', 'No coordinates found');
    return { startTime: null, endTime: null };
  }

  const first = coords[0];
  const last = coords[coords.length - 1];

  appDebugLog.info('TrackMetadataService', 'First coordinate:', { first, length: first.length });
  appDebugLog.info('TrackMetadataService', 'Coordinate count:', coords.length);

  // M coordinate is at index 3 (XYZM: lon, lat, alt, time)
  // OpenLayers stores time as seconds since epoch, we need milliseconds
  let startTime = null;
  let endTime = null;

  // Check for timestamp in first coordinate
  if (first.length >= 4 && first[3] !== null && first[3] !== undefined && first[3] !== 0) {
    // If timestamp is already in milliseconds (> year 2000 in ms), use as is
    // Otherwise assume it's seconds and convert
    startTime = first[3] > 946684800000 ? first[3] : first[3] * 1000;
    appDebugLog.info('TrackMetadataService', 'Extracted startTime:', { startTime, rawValue: first[3] });
  }

  // Check for timestamp in last coordinate
  if (last.length >= 4 && last[3] !== null && last[3] !== undefined && last[3] !== 0) {
    endTime = last[3] > 946684800000 ? last[3] : last[3] * 1000;
    appDebugLog.info('TrackMetadataService', 'Extracted endTime:', { endTime, rawValue: last[3] });
  }

  return { startTime, endTime };
}

// ============================================================================
// Distance Calculation
// ============================================================================

/**
 * Calculate total distance from coordinates using OpenLayers geodesic calculation.
 * Delegates to Utils.calculateDistanceFromCoordinates() for consistent results
 * across the application.
 *
 * @param {Array} coordinates - Array of coordinate points [lon, lat, ...]
 * @returns {Object} Distance in various units: km, m, feet, inches, miles, seamiles, yards
 */
export function calculateTotalDistance (coordinates) {
  return calculateDistanceFromCoordinates(coordinates);
}

// ============================================================================
// Elevation Data
// ============================================================================

/**
 * Extract elevation data from coordinates
 * Elevation is at index 2: [lon, lat, elevation, time?]
 *
 * @param {Array} coordinates - Array of coordinate points
 * @returns {{ minElevation: number, maxElevation: number, elevationGain: number, elevationLoss: number }}
 */
export function extractElevationData (coordinates) {
  const coords = flattenCoordinates(coordinates);

  if (!coords || coords.length < 2) {
    return { minElevation: 0, maxElevation: 0, elevationGain: 0, elevationLoss: 0 };
  }

  let minElevation = Infinity;
  let maxElevation = -Infinity;
  let elevationGain = 0;
  let elevationLoss = 0;
  let prevElevation = null;

  for (const coord of coords) {
    // Elevation is at index 2 (3rd value): [lon, lat, elevation]
    const elevation = coord.length >= 3 ? coord[2] : null;

    if (elevation !== null && elevation !== undefined && !isNaN(elevation)) {
      // Update min/max
      if (elevation < minElevation) minElevation = elevation;
      if (elevation > maxElevation) maxElevation = elevation;

      // Calculate gain/loss
      if (prevElevation !== null) {
        const diff = elevation - prevElevation;
        if (diff > 0) {
          elevationGain += diff;
        }
        else {
          elevationLoss += Math.abs(diff);
        }
      }
      prevElevation = elevation;
    }
  }

  // Handle case where no elevation data was found
  if (minElevation === Infinity) minElevation = 0;
  if (maxElevation === -Infinity) maxElevation = 0;

  return {
    minElevation: Math.round(minElevation),
    maxElevation: Math.round(maxElevation),
    elevationGain: Math.round(elevationGain),
    elevationLoss: Math.round(elevationLoss)
  };
}

// ============================================================================
// Duration Utilities
// ============================================================================

/**
 * Calculate duration in seconds from start and end timestamps
 *
 * @param {number} startTime - Start timestamp in milliseconds
 * @param {number} endTime - End timestamp in milliseconds
 * @returns {number} Duration in seconds
 */
export function calculateDurationSeconds (startTime, endTime) {
  if (!startTime || !endTime || endTime <= startTime) {
    return 0;
  }
  return Math.floor((endTime - startTime) / 1000);
}

/**
 * Format duration in seconds to HH:MM:SS string
 *
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export function formatDuration (seconds) {
  if (!seconds || seconds <= 0) {
    return '00:00:00';
  }

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

// ============================================================================
// Full Metadata Extraction
// ============================================================================

/**
 * Extract and calculate all metadata from a GeoJSON track
 *
 * @param {Object} geojson - GeoJSON FeatureCollection with track data
 * @returns {Object} Extracted metadata
 */
export function extractTrackMetadata (geojson) {
  if (!geojson?.features?.[0]?.geometry?.coordinates) {
    return null;
  }

  const feature = geojson.features[0];
  const coordinates = feature.geometry.coordinates;
  const props = feature.properties || {};

  // Try to extract timestamps from multiple sources:
  // 1. XYZM coordinates (4th value)
  let { startTime, endTime } = extractTimestampsFromCoordinates(coordinates);

  // 2. If no timestamps in coordinates, check properties (including metadataTime from GPX)
  if (!startTime) {
    // Check common timestamp property names - metadataTime first (from GPX parsing)
    const timeFields = ['metadataTime', 'when', 'time', 'timestamp', 'startTime', 'begin', 'start_time', 'recorded', 'date', 'datetime'];
    for (const field of timeFields) {
      if (props[field]) {
        const parsed = new Date(props[field]);
        if (!isNaN(parsed.getTime())) {
          startTime = typeof props[field] === 'number' ? props[field] : parsed.getTime();
          appDebugLog.info('TrackMetadataService', 'Found startTime in properties.' + field + ':', startTime);
          break;
        }
      }
    }
  }

  // 3. Check for endTime in properties
  if (!endTime && startTime) {
    const endTimeFields = ['endTime', 'end', 'end_time'];
    for (const field of endTimeFields) {
      if (props[field]) {
        const parsed = new Date(props[field]);
        if (!isNaN(parsed.getTime())) {
          endTime = parsed.getTime();
          break;
        }
      }
    }
    // Default endTime to startTime if not found
    if (!endTime) endTime = startTime;
  }

  // 4. Check coordinatesExtended array for timestamps
  if (!startTime && props.coordinatesExtended && Array.isArray(props.coordinatesExtended)) {
    const firstExt = props.coordinatesExtended[0];
    const lastExt = props.coordinatesExtended[props.coordinatesExtended.length - 1];
    if (firstExt?.timestamp) {
      startTime = new Date(firstExt.timestamp).getTime();
      appDebugLog.info('TrackMetadataService', 'Found startTime in coordinatesExtended:', startTime);
    }
    if (lastExt?.timestamp && !endTime) {
      endTime = new Date(lastExt.timestamp).getTime();
    }
  }

  // Calculate duration
  const durationInSeconds = calculateDurationSeconds(startTime, endTime);
  const timerDisplay = formatDuration(durationInSeconds);

  // Calculate distance using OpenLayers via Utils.js
  const totalDistance = calculateTotalDistance(coordinates);

  // Extract elevation data
  const elevation = extractElevationData(coordinates);

  // Extract title from properties if available
  const title = props.name || props.title || null;

  // Extract activity type
  const typeTitle = props.typeTitle || props.type || 'Imported';

  appDebugLog.info('TrackMetadataService', 'Final extraction:', { startTime, endTime, durationInSeconds, distanceKm: totalDistance.km, elevation });

  return {
    startTime,
    endTime,
    durationInSeconds,
    durationStartStop: durationInSeconds,
    timerDisplay,
    totalDistance,
    distanceKm: totalDistance.km,
    distanceM: totalDistance.m,
    minElevation: elevation.minElevation,
    maxElevation: elevation.maxElevation,
    elevationGain: elevation.elevationGain,
    elevationLoss: elevation.elevationLoss,
    title,
    typeTitle,
    note: props.desc || props.description || props.note || null
  };
}

// ============================================================================
// Default Export
// ============================================================================

export default {
  extractTimestampsFromCoordinates,
  calculateTotalDistance,
  extractElevationData,
  calculateDurationSeconds,
  formatDuration,
  extractTrackMetadata
};
